import { Config } from "../../../config/Config";

/**
 * UI delivery address: Address line 1 (`street_line`) and line 2 (`appartment`) only.
 * Does not append city, state, zip, or country from separate fields.
 */
export function formatAddressLine1And2(res: any): string {
  if (!res || typeof res !== "object") return "—";
  const line1 = String(res.street_line ?? "").trim();
  const line2 = String(res.appartment ?? "").trim();
  if (!line1 && !line2) return "—";
  if (!line2) return line1;
  if (!line1) return line2;
  if (line2.toLowerCase() === line1.toLowerCase()) return line1;
  return `${line1}, ${line2}`.replace(/\s+/g, " ").trim();
}

/** @deprecated Prefer `formatAddressLine1And2` — kept for existing imports. */
export function formatAddressListRow(res: any): string {
  return formatAddressLine1And2(res);
}

/** Address book / quote header / delivery picker label text. */
export function formatAddressBookRow(res: any): string {
  return formatAddressLine1And2(res);
}

export function formatQuotationAddress(data: any): string {
  if (!data || typeof data !== "object") return "—";
  const a = data.address_detail;
  return formatAddressLine1And2(
    a
      ? {
          ...a,
          street_line: a.street_line ?? data.street_line,
          appartment: a.appartment ?? data.appartment,
        }
      : {
          street_line: data.street_line,
          appartment: data.appartment,
        },
  );
}

export function getQuoteLineImageUrl(media: any): string | null {
  const m = Array.isArray(media) ? media[0] : media;
  const path = m?.thumb_file_path || m?.file_path;
  if (!path || typeof path !== "string") return null;
  if (path.startsWith("http")) return path;
  const base = (Config.REACT_APP_API_BASE_URL || "").replace(/\/?api\/?$/i, "");
  if (!base) return path;
  return `${base.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
}

/** Shown when admin marked a line unavailable (N/A qty). */
export const QUOTE_UNAVAILABLE_DASH = "---";

export function isQuoteLineUnavailable(row: any): boolean {
  if (row?.is_unavailable == 1 || row?.is_unavailable === "1" || row?.is_unavailable === true) {
    return true;
  }
  const cq = row?.common_quantity;
  if (cq == null || cq === "") return false;
  const n = Number(cq);
  return Number.isFinite(n) && n <= 0;
}

export function formatQuotedQtyDisplay(row: any): string {
  if (isQuoteLineUnavailable(row)) return "N/A";
  const disp = row?.display_quantity;
  if (disp != null && String(disp).trim() !== "") {
    const s = String(disp).trim();
    if (s.toUpperCase() === "N/A" || s.toUpperCase() === "NA") return "N/A";
    return s;
  }
  const raw = row?.new_quantity ?? row?.common_quantity ?? row?.quantity;
  if (raw == null) return "N/A";
  const s = String(raw).trim();
  return s === "" ? "N/A" : s;
}

export function getQuotedLineQtyNumber(row: any): number {
  if (isQuoteLineUnavailable(row)) return 0;
  const raw = row?.common_quantity ?? row?.new_quantity ?? row?.quantity;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function getQuotedLineExtendedAmount(row: any): number | null {
  if (isQuoteLineUnavailable(row)) return null;
  const up = Number(row?.per_product_price);
  const qty = getQuotedLineQtyNumber(row);
  if (!Number.isFinite(up) || up <= 0 || qty <= 0) return null;
  return up * qty;
}

/** True when admin entered unit price (or ETA) on a line. */
export function hasQuoteLineAdminPricing(row: any): boolean {
  if (!row || typeof row !== "object") return false;
  if (isQuoteLineUnavailable(row)) return true;
  const up = Number(row?.per_product_price);
  if (Number.isFinite(up) && up > 0) return true;
  const eta = row?.eta_day;
  return eta != null && String(eta).trim() !== "";
}

/** True when any quote line has admin pricing. */
export function quotationHasLineAdminPricing(data: any): boolean {
  const rows = data?.quotes_detail;
  if (!Array.isArray(rows) || rows.length === 0) return false;
  return rows.some((row: any) => hasQuoteLineAdminPricing(row));
}

/**
 * True when price-breakdown has at least one real money value
 * (subtotal, freight, other charges, tax, or grand total).
 */
export function quotationHasPriceBreakdownValues(data: any): boolean {
  if (!data || typeof data !== "object") return false;
  const nums = [
    Number(data.total_product_price),
    Number(data.total_shipping_price),
    Number(data.other_charges),
    Number(data.total_amount),
  ];
  if (nums.some((n) => Number.isFinite(n) && n > 0)) return true;

  if (data?.tax_amount && data?.total_amount) {
    const base =
      Number(data.total_shipping_price || 0) +
      Number(data.total_product_price || 0) +
      Number(data.other_charges || 0);
    const tax = (base * Number(data.tax_amount)) / 100;
    if (Number.isFinite(tax) && tax > 0) return true;
  }

  return quotationHasLineAdminPricing(data);
}

export function quoteRowSearchBlob(res: any, brandLabel: string): string {
  return [
    brandLabel,
    res?.part_number,
    res?.description,
    res?.serial_number,
    res?.model_number,
    res?.other_brand_name,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export type OtherChargeLine = { label: string; amount: string };

/** Parses admin `charges_reason` (e.g. "Fee (100.00); Tax (50.00)"). */
export function parseOtherChargesLines(reason?: string): OtherChargeLine[] {
  if (!reason?.trim()) return [];
  return reason
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const match = part.match(/^(.*)\(([\d.,]+)\)\s*$/);
      if (match) {
        return { label: match[1].trim(), amount: match[2].trim() };
      }
      return { label: part, amount: "0.00" };
    });
}

/** Numeric value from a charge line amount (strips $, €, commas). */
export function parseChargeLineAmount(amount: string): number {
  const n = Number(String(amount).replace(/[$€,\s]/g, ""));
  return Number.isFinite(n) ? n : 0;
}
